import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';

import Booking from '../../../../core/types/model/Booking';
import { BookingStatus } from '../../../../core/enums/BookingStatus';
import { Color } from '../../../../core/enums/Color';
import { StateStatus } from '../../../../core/enums/StateStatus';
import { formatBookingDuration } from '../../../../core/utils/date';

import { RootState } from '../../../../core/state/state';
import { Actions, Actions as BookingActions } from '../../../../core/state/bookings/actions';
import { selectEquipmentById } from '../../../../core/state/equipment/selectors';
import { isReadAllRole, selectIsUserAdmin } from '../../../../core/state/user/selectors';
import { getBookingConfirmation, setBookingDoneStatus, setBookingStatus, unblockDate } from '../../../../core/state/bookings/asyncActions';
import { getStateStatus } from '../../../../core/state/bookings/selectors';
import { spawnGlobalNotification } from '../../../../core/state/global-notifications/asyncActions';
import { getBookingNotification, SubmitAction } from '../../helpers';

import Button, { ButtonType } from '../../../../components/form/button/Button';
import Modal from '../../../components/modal/Modal';
import Badge from '../../../components/badge/Badge';
import DeliveryZoneBadge from '../../../components/delivery-zone-badge/DeliveryZoneBadge';
import BookingStatusBadge from '../../../components/booking-status-badge/BookingStatusBadge';
import BookingCancellationModal from '../booking-cancelation/BookingCancellationModal';
import PendingBookingCountdown from '../../../components/pending-booking-countdown/PendingBookingCountdown';
import BookingChangeStatusModal from '../booking-change-status/BookingChangeStatusModal';
import BookingRejectionModal from '../booking-rejection/BookingRejectionModal';
import Icon from '../../../../components/icon/Icon';
import InteractiveElement from '../../../../components/interactive-element/InteractiveElement';
import { DATE_FORMAT_EU_DOT, DATE_FORMAT_HOURS_MINUTES } from '../../../../core/constants/date';
import { isMobileScreenSize } from '../../../../core/utils/isMobileScreenSize';
import { Size } from '../../../../core/enums/Size';
import { GlobalNotificationTypes } from '../../../../core/enums/GlobalNotificationTypes';
import { NotificationPosition } from '../../../../core/state/global-notifications/types';
import { useInternalUser } from '../../../../core/hooks/useInternalUser';
import { ISO_DATE_REGEX } from '../../../../core/constants/regex';

import './BookingDetailsModal.scss';

interface Props {
  booking: Booking;
  fetchBookings?: any;
}

const BookingDetailsModal: React.FC<Props> = ({ booking, fetchBookings }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isAdmin = useSelector(selectIsUserAdmin);
  const [submitAction, setSubmitAction] = useState<SubmitAction>();
  const stateStatus = useSelector(getStateStatus);
  const isUserReader = useSelector(isReadAllRole);
  const isMobileScreen = isMobileScreenSize();
  const isInternal = useInternalUser();
  const isEditable = booking.confirmationStatus === BookingStatus.Open && booking.isInternal && isInternal;
  const language = useSelector((state: RootState) => state.user.userInfo?.lang);

  const isPendingStatus = () => booking.confirmationStatus === BookingStatus.Pending;
  const isBlocked = booking.confirmationStatus === BookingStatus.Blocked;
  const [isCancellationModalOpen, setCancellationModalOpen] = useState<boolean>(false);
  const [isChangeToDoneModalOpen, setChangeToDoneModalOpen] = useState<boolean>(false);
  const [isChangeToOpenModalOpen, setChangeToOpenModalOpen] = useState<boolean>(false);
  const [isRejectionModalOpen, setRejectionModalOpen] = useState<boolean>(false);
  const [showPendingOptions, setShowPendingOptions] = useState(false);
  const canExternalUserCancel = !isInternal && (isPendingStatus() || booking.confirmationStatus === BookingStatus.Open);

  const closeCancellationModal = useCallback(() => {
    setCancellationModalOpen(false);
  }, []);

  const equipment = useSelector((state: RootState) => {
    if (!booking?.transportEquipmentId) {
      return null;
    }
    return selectEquipmentById(state, booking?.transportEquipmentId);
  });

  const closeModal = useCallback(() => {
    dispatch(BookingActions.closeDetails());
  }, [dispatch]);

  const title = (
    <>
      <div className="booking-details__title">
        <div className={`booking-details__status ${language === 'de-DE' && isPendingStatus() ? 'flex-column' : ''}`}>
          <BookingStatusBadge rejectionReason={booking.rejectionReason} status={booking.confirmationStatus} />
          {booking.confirmationStatus === BookingStatus.Pending && booking.createdOn && (
            <PendingBookingCountdown labelSize={Size.Large} size={Size.Medium} booking={booking} dispatchStatusUpdate={false} withCountdownText />
          )}
          {booking?.confirmationStatus === BookingStatus.Done && booking?.confirmedOn && (
            <Badge
              color={Color.Green}
              label={` ${t('booking.modal.date.checkin')}: ${format(new Date(booking.confirmedOn.replace(/-/g, '/')), DATE_FORMAT_EU_DOT)} ${format(
                new Date(booking.confirmedOn.replace(ISO_DATE_REGEX, '/')),
                DATE_FORMAT_HOURS_MINUTES
              )}`}
              labelSize={Size.Large}
            />
          )}
        </div>
        <div>{booking.confirmedByName}</div>
      </div>
    </>
  );

  const approveBooking = async () => {
    setSubmitAction('approve');
    dispatch(setBookingStatus(booking, BookingStatus.Open));
  };

  const cancelBooking = () => {
    setCancellationModalOpen(true);
  };

  const changeBookingToDone = (bookingDoneAt?: string) => {
    if (!bookingDoneAt) return;

    setSubmitAction('done');
    dispatch(
      setBookingDoneStatus(booking, {
        status: BookingStatus.Done,
        bookingDoneAt
      })
    );
  };

  const changeBookingToOpen = () => {
    setSubmitAction('open');
    dispatch(setBookingStatus(booking, BookingStatus.Open));
  };

  useEffect(() => {
    if (submitAction && stateStatus === StateStatus.Loaded) {
      const notification = getBookingNotification(submitAction, t);
      if (notification) {
        dispatch(spawnGlobalNotification(notification));
      }
      closeModal();
    }
  }, [closeModal, dispatch, stateStatus, submitAction, t]);

  const buttonType: ButtonType[] = isMobileScreen ? ['outline', 'block'] : ['outline'];

  const unblockSlot = async () => {
    await dispatch(unblockDate(String(booking.id)));
    closeModal();
    if (fetchBookings) {
      fetchBookings();
    }
    dispatch(
      spawnGlobalNotification({
        type: GlobalNotificationTypes.Success,
        postition: NotificationPosition.Top,
        message: t('projectSettings.blockBookingSlots.unblockDate.notification')
      })
    );
  };

  const openEditBookingModal = useCallback(() => {
    dispatch(Actions.openEditModal());
  }, [dispatch]);

  const buttons = (
    <div className={`d-flex ${isEditable ? 'justify-content-between' : 'justify-content-end'}`}>
      {isEditable && (
        <div className="booking-details__edit-button">
          <Button buttonTypes={['transparent']} onClick={() => openEditBookingModal()}>
            <Icon icon="edit" size="small" svg />
          </Button>
        </div>
      )}

      {isAdmin && (
        <div className={`${isMobileScreen ? 'd-flex flex-column modal-buttons--mobile w-100' : 'modal-buttons'}`}>
          {!isUserReader && (
            <>
              {booking.confirmationStatus === BookingStatus.Open && (
                <Button buttonTypes={buttonType} color={Color.Green} onClick={() => setChangeToDoneModalOpen(true)}>
                  {t('booking.done.button')}
                </Button>
              )}
              {booking.confirmationStatus === BookingStatus.Done && (
                <Button buttonTypes={buttonType} color={Color.Red} onClick={() => setChangeToOpenModalOpen(true)}>
                  {t('booking.open.button')}
                </Button>
              )}
              {booking.confirmationStatus !== BookingStatus.Done && !isBlocked && !isMobileScreen && (
                <Button
                  buttonTypes={buttonType}
                  color={Color.Red}
                  onClick={booking.confirmationStatus === BookingStatus.Pending ? () => setRejectionModalOpen(true) : cancelBooking}
                >
                  {booking.confirmationStatus === BookingStatus.Pending ? t('booking.reject.button') : t('booking.cancel.button')}
                </Button>
              )}

              {isMobileScreen && !isPendingStatus() && booking.confirmationStatus !== BookingStatus.Done && !isBlocked && (
                <Button buttonTypes={buttonType} color={Color.Red} onClick={cancelBooking}>
                  {t('booking.cancel.button')}
                </Button>
              )}

              {isBlocked && (
                <Button buttonTypes={buttonType} color={Color.Blue} onClick={unblockSlot}>
                  {t('projectSettings.blockBookingSlots.button.unblock')}
                </Button>
              )}
            </>
          )}

          {isPendingStatus() && (
            <>
              {!showPendingOptions && isMobileScreen ? (
                <>
                  <Button buttonTypes={buttonType} color={Color.Green} onClick={approveBooking}>
                    {t('common.confirm')}
                  </Button>
                  <Button buttonTypes={buttonType} onClick={() => setShowPendingOptions(true)}>
                    {t('booking.modal.moreOptions')}
                  </Button>
                </>
              ) : (
                <>
                  {isMobileScreen && (
                    <Button buttonTypes={buttonType} color={Color.Red} onClick={cancelBooking}>
                      {t('booking.cancel.button')}
                    </Button>
                  )}
                  <Button buttonTypes={buttonType} color={Color.Blue} onClick={() => dispatch(BookingActions.openEditModal())}>
                    {t('booking.modal.editAndConfirm')}
                  </Button>
                  <Button buttonTypes={buttonType} color={Color.Green} onClick={approveBooking}>
                    {t('common.confirm')}
                  </Button>
                </>
              )}
            </>
          )}
        </div>
      )}

      {canExternalUserCancel && (
        <Button buttonTypes={buttonType} color={Color.Red} onClick={cancelBooking}>
          {t('booking.cancel.button')}
        </Button>
      )}
    </div>
  );

  return (
    <>
      <Modal
        isFullScreen={isMobileScreen}
        isOpen
        closeModal={closeModal}
        title={
          <div className="modal-title d-flex align-items-center">
            {isMobileScreen && showPendingOptions && (
              <Button buttonTypes={['border-icon', 'transparent', 'extra-small']} onClick={() => setShowPendingOptions(false)} className="mr-2">
                <Icon icon="arrowLeft" size="extra-small" svg />
              </Button>
            )}
            <span>{t('booking.details.title')}</span>
          </div>
        }
        toolbar={buttons}
        transparentOverlay
        className={`booking-details ${isPendingStatus() && 'booking-details--pending'}`}
      >
        <div className={`booking-details ${isMobileScreen && 'booking-details--mobile'}`}>
          <div className="booking-details__row">{title}</div>
          {!isBlocked && (
            <div className="booking-details__row">
              <p className="booking-details__label">{t('booking.title')}</p>
              <p className="booking-details__value">{booking.title}</p>
            </div>
          )}

          <div className="booking-details__row form-row">
            <div>
              <p className="booking-details__label">{t(isBlocked ? 'booking.blockedDate' : 'booking.deliveryDate')}</p>
              <p className="booking-details__value">{`${format(
                booking.confirmationStatus === BookingStatus.Done ? new Date(booking.confirmedOn) : new Date(booking.date),
                DATE_FORMAT_EU_DOT
              )}`}</p>
            </div>

            <div>
              <p className="booking-details__label">{t(isBlocked ? 'booking.blockedTime' : 'booking.deliveryTime')}</p>
              <p className="booking-details__value">
                {booking.confirmationStatus === BookingStatus.Done
                  ? format(new Date(booking.confirmedOn.replace(ISO_DATE_REGEX, '/')), DATE_FORMAT_HOURS_MINUTES)
                  : formatBookingDuration(booking)}
              </p>
            </div>
          </div>

          {isMobileScreen ? (
            <>
              <div className="booking-details__row">
                <p className="booking-details__label">{t('booking.deliveryZone')}</p>
                <div className="booking-details__value">
                  <DeliveryZoneBadge isBlocked={isBlocked} labelSize={Size.Medium} id={booking.deliveryZoneId} />
                </div>
              </div>
              {!isBlocked && (
                <div className="booking-details__row">
                  <p className="booking-details__label">{t('booking.transport')}</p>
                  <p className="booking-details__value">{equipment?.name}</p>
                </div>
              )}
            </>
          ) : (
            <div className="booking-details__row form-row">
              <div>
                <p className="booking-details__label">{t('booking.deliveryZone')}</p>
                <div className="booking-details__value">
                  <DeliveryZoneBadge isBlocked={isBlocked} labelSize={Size.Medium} id={booking.deliveryZoneId} />
                </div>
              </div>
              {!isBlocked && (
                <div>
                  <p className="booking-details__label">{t('booking.transport')}</p>
                  <p className="booking-details__value">{equipment?.name}</p>
                </div>
              )}
            </div>
          )}

          {booking.pin && (
            <div className="booking-details__row">
              <p className="booking-details__label">{t('booking.accessCode')}</p>
              <div className="booking-details__value">
                <Badge color={Color.Blue} label={booking.pin} boldLabel />
              </div>
            </div>
          )}
          {!isBlocked && (
            <div className="booking-details__row">
              <p className="booking-details__label">{t('booking.contactPersonSubcontractor')}</p>
              <p className="booking-details__value">{booking.contactPersonText}</p>
            </div>
          )}

          {isBlocked && (
            <div className="booking-details__row">
              <p className="booking-details__label">{t('common.createdBy')}</p>
              <p className="booking-details__value">{booking.createdBy}</p>
            </div>
          )}

          <div className="booking-details__row">
            <p className="booking-details__label">{t(isBlocked ? 'common.comment' : 'booking.description')}</p>
            <p className="booking-details__value">{booking.description}</p>
          </div>

          {booking.confirmationStatus === BookingStatus.Open && (
            <div>
              <p className="booking-details__label">{t('booking.confirmationFile.title')}</p>
              <InteractiveElement className="d-flex align-items-center" onClick={() => dispatch(getBookingConfirmation(booking.id))}>
                <Icon icon="file" color={Color.Black} withCard svg />
                <span className="pl-2">{t('booking.confirmationFile.link')}</span>
              </InteractiveElement>
              {isMobileScreen && (
                <Button
                  className="booking-details__file-download mt-3"
                  buttonTypes={['primary', 'block']}
                  color={Color.Blue}
                  onClick={() => dispatch(getBookingConfirmation(booking.id))}
                >
                  {t('common.downloadFile')}
                  <Icon className="booking-details__file-download-icon" icon="download" svg />
                </Button>
              )}
            </div>
          )}
        </div>
      </Modal>
      {isCancellationModalOpen && (
        <BookingCancellationModal
          booking={booking}
          isOpen={isCancellationModalOpen}
          closeModal={closeCancellationModal}
          onSubmit={() => {
            setSubmitAction('cancel');
          }}
        />
      )}
      {isRejectionModalOpen && (
        <BookingRejectionModal
          booking={booking}
          isOpen={isRejectionModalOpen}
          closeModal={() => setRejectionModalOpen(false)}
          onSubmit={() => {
            setSubmitAction('reject');
          }}
        />
      )}
      {isChangeToDoneModalOpen && (
        <BookingChangeStatusModal
          message="booking.changeToDone"
          isOpen={isChangeToDoneModalOpen}
          closeModal={() => setChangeToDoneModalOpen(false)}
          onConfirm={changeBookingToDone}
          showDateTimeFields
        />
      )}

      {isChangeToOpenModalOpen && (
        <BookingChangeStatusModal
          message="booking.changeToOpen"
          isOpen={isChangeToOpenModalOpen}
          closeModal={() => setChangeToOpenModalOpen(!isChangeToOpenModalOpen)}
          onConfirm={() => {
            changeBookingToOpen();
          }}
        />
      )}
    </>
  );
};

export default BookingDetailsModal;
